import { FunctionComponent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ROUTE_CONFIG } from "@/routes/config";

import { TrackerEntityKind } from "@/enum/tracker.enum";

import { useGetLeanListsQuery } from "@/pages/Private/redux/list/list.api";
import { useDebouncedValue } from "@/pages/Private/pages/Score/utils/debounceValue";
import { useGetTrackerListImportsQuery } from "@/pages/Private/redux/profileTracker/trackerSources.api";

import { List } from "../../schema/list";

import { TrackerAddedSources } from "./TrackerAddedSources";
import { TrackerListImportForm } from "./TrackerListImportForm";
import { TrackerSourceInfoBanner } from "./TrackerSourceInfoBanner";
import { TrackerSourceMode, TrackerSourceModeSwitch } from "./TrackerSourceModeSwitch";
import { TrackerUploadSource } from "./TrackerUploadSource";

interface TrackerSourcesStepProps {
	list: List;
}

/**
 * The Sources step of a Profile Tracker list (PREC-3417).
 *
 * Two columns, mirroring the mockup: what you are adding on the left, what is already feeding
 * the tracker on the right. The explanation sits above both, because the ticket's complaint
 * was that users did not know a clean identifier was required until nothing got tracked.
 */
export const TrackerSourcesStep: FunctionComponent<TrackerSourcesStepProps> = ({ list }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [mode, setMode] = useState(TrackerSourceMode.PEOPLE);
	const [listSearch, setListSearch] = useState("");

	const { data: listImports } = useGetTrackerListImportsQuery(list.id);

	// The picker searches server-side rather than filtering the page it happens to hold: an
	// account with hundreds of lists would otherwise only ever find the first 50.
	const debouncedListSearch = useDebouncedValue(listSearch, 400);

	// Any list can be a source except this tracker itself — importing from yourself is a no-op
	// the API refuses, so it is not offered.
	const { data: leanLists, isFetching: isFetchingLists } = useGetLeanListsQuery(
		{ page: 1, limit: 50, archived: false, searchValue: debouncedListSearch || undefined },
		{ skip: mode !== TrackerSourceMode.IMPORT }
	);

	const sourceLists = useMemo(
		() =>
			(leanLists?.data ?? [])
				.filter((candidate) => candidate.id !== list.id)
				.map((candidate) => ({ id: candidate.id, name: candidate.name })),
		[leanLists, list.id]
	);

	const trackerFiles = useMemo(
		() => (list.trackers ?? []).flatMap((tracker) => tracker.trackerFiles ?? []),
		[list.trackers]
	);

	return (
		<div>
			<TrackerSourceInfoBanner />

			<div className="grid grid-cols-1 items-start gap-6 tb:grid-cols-2">
				<div className="rounded-xl border border-border-light bg-white">
					<div className="border-b border-border-light px-6 py-4">
						<h2 className="text-md font-bold">{t("profileTracker.sources.addSource")}</h2>
					</div>
					<div className="flex flex-col gap-4 px-6 py-4">
						<TrackerSourceModeSwitch mode={mode} onChange={setMode} />

						{mode === TrackerSourceMode.IMPORT ? (
							<TrackerListImportForm
								isSearchingLists={isFetchingLists}
								listId={list.id}
								sourceLists={sourceLists}
								onSearchLists={setListSearch}
							/>
						) : (
							<TrackerUploadSource
								entityKind={
									mode === TrackerSourceMode.COMPANIES
										? TrackerEntityKind.ORGANIZATION
										: TrackerEntityKind.PERSON
								}
								listId={list.id}
								onCreateListInstead={() => navigate(ROUTE_CONFIG.NEW_LIST)}
							/>
						)}
					</div>
				</div>

				<div className="rounded-xl border border-border-light bg-white">
					<div className="border-b border-border-light px-6 py-4">
						<h2 className="text-md font-bold">
							{t("profileTracker.sources.added.title")}{" "}
							<span className="font-normal text-text-meta">
								({trackerFiles.length + (listImports?.length ?? 0)})
							</span>
						</h2>
					</div>
					<div className="px-6 py-4">
						<TrackerAddedSources files={trackerFiles} listImports={listImports ?? []} />
					</div>
				</div>
			</div>
		</div>
	);
};
